// client/src/utils/passwordless.js — the browser half of password-free sign-in
// (server: routes/passwordless.js). Used by the login page and the Password-free
// Sign-in page.
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';

/** What the login page may offer: { tailnet: {available, signIn, baseUrl}, passkeys: {enabled} }. */
export async function loadPasswordlessConfig() {
    try {
        const r = await fetch('/api/auth/passwordless/config');
        if (!r.ok) return null;
        return await r.json();
    } catch (e) {
        return null;
    }
}

/**
 * Ask the office tailnet host for a sign-in ticket. Works only on a device that is on the
 * office Tailscale network; anywhere else it fails quickly and quietly (null).
 * @returns {Promise<{ticket, peer, device:{name, os}, logins:{userId, username, name, device}[]}|null>}
 */
export async function fetchTailnetTicket(baseUrl, timeoutMs = 2500) {
    if (!baseUrl) return null;
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctl && ctl.abort(), timeoutMs);
    try {
        const r = await fetch(`${baseUrl}/api/auth/tailnet/ticket`, {
            method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store',
            signal: ctl ? ctl.signal : undefined,
        });
        if (!r.ok) return null;
        const data = await r.json();
        return data && data.ticket ? data : null;
    } catch (e) {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

async function postJson(url, body, token) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body || {}) });
    let data = {};
    try { data = await r.json(); } catch (e) { /* non-JSON body */ }
    if (!r.ok) throw new Error(data.message || `Request failed (${r.status})`);
    return data;
}

/** One-tap sign-in from a trusted tailnet device: a fresh ticket, then the session. */
export async function tailnetSignIn(baseUrl, userId) {
    const t = await fetchTailnetTicket(baseUrl, 5000);
    if (!t) throw new Error('Could not reach the office Tailscale network from this device. Is Tailscale switched on?');
    return postJson('/api/auth/tailnet/login', { ticket: t.ticket, userId });
}

// The page-visit door (26 Oct 2026): newer Chrome blocks the background read above unless the
// person answers a "local network" prompt, so the button opens the tailnet host as a page; it
// redirects back to /portal#tailnet=<ticket> (a fragment, never sent to a server).
export const tailnetStartUrl = (baseUrl) => (baseUrl ? `${baseUrl}/api/auth/tailnet/start` : null);

/** Read (and clear) a ticket or error the tailnet host handed back in the address. */
export function takeTailnetReturn() {
    if (typeof window === 'undefined' || !window.location.hash) return null;
    const p = new URLSearchParams(window.location.hash.slice(1));
    const ticket = p.get('tailnet');
    const error = p.get('tailnet_error');
    if (!ticket && !error) return null;
    try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) { /* old browser */ }
    return { ticket, error };
}

export const tailnetLoginsFor = (ticket) => postJson('/api/auth/tailnet/logins', { ticket });
export const tailnetSignInWith = (ticket, userId) => postJson('/api/auth/tailnet/login', { ticket, userId });

export const passkeysSupported = () => {
    try { return browserSupportsWebAuthn(); } catch (e) { return false; }
};

// The browser's own "cancelled" answer is not an error worth a red message.
const cancelled = (e) => e && (e.name === 'NotAllowedError' || e.name === 'AbortError');

/** Usernameless passkey sign-in; resolves with the session, or null if the person cancelled. */
export async function passkeySignIn() {
    const { challengeId, options } = await postJson('/api/auth/passkeys/login/options', {});
    let response;
    try {
        response = await startAuthentication({ optionsJSON: options });
    } catch (e) {
        if (cancelled(e)) return null;
        throw e;
    }
    return postJson('/api/auth/passkeys/login/verify', { challengeId, response });
}

/** Add a passkey to the signed-in login; resolves with the server answer, or null if cancelled. */
export async function registerPasskey(name) {
    const token = localStorage.getItem('jwtToken');
    const { challengeId, options } = await postJson('/api/auth/passkeys/register/options', {}, token);
    let response;
    try {
        response = await startRegistration({ optionsJSON: options });
    } catch (e) {
        if (cancelled(e)) return null;
        if (e && e.name === 'InvalidStateError') throw new Error('This device already has a passkey for your login.');
        throw e;
    }
    return postJson('/api/auth/passkeys/register/verify', { challengeId, response, name }, token);
}

/** A sensible default name for a new passkey, from the browser's description of itself. */
export function guessDeviceName() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/android/i.test(ua)) return 'Android phone';
    if (/iphone/i.test(ua)) return 'iPhone';
    if (/ipad/i.test(ua)) return 'iPad';
    if (/macintosh/i.test(ua)) return 'Mac';
    if (/windows/i.test(ua)) return 'Windows computer';
    return 'My device';
}
